import { useMemo } from 'react';
import {
  type AssignmentDto,
  MONTHS_SHORT,
  dayFromLocalDate,
  daysInMonth,
  dominantPersonOfDayFromAssignments,
  formatMonthYear,
  intervalsOverlap,
  monthSlotRange,
  weekdayOfDay,
} from '@betreuung/shared';
import { cn } from '@/lib/utils';
import { personVars } from '@/lib/personColor';
import type { PersonLookup } from '@/lib/persons';

export interface MiniMonthProps {
  year: number;
  month: number;
  /** Assignments of the whole year; narrowed to this month internally. */
  assignments: readonly AssignmentDto[];
  persons: PersonLookup;
  /** Today's day index, passed in so the year view reads the clock only once. */
  todayDay: number;
  onSelect: (year: number, month: number) => void;
}

/**
 * One month of the year overview: a 12×12px tile per day in the full colour of
 * that day's dominant person. Individual days are deliberately not interactive —
 * the whole month is a single button into the month view.
 */
export function MiniMonth({
  year,
  month,
  assignments,
  persons,
  todayDay,
  onSelect,
}: MiniMonthProps) {
  const firstDay = dayFromLocalDate(year, month, 1);
  const dayCount = daysInMonth(year, month);

  const owners = useMemo(() => {
    const { fromSlot, toSlot } = monthSlotRange(year, month);
    // Narrowing the year's assignments once keeps the per-day scan cheap.
    const inMonth = assignments.filter((assignment) =>
      intervalsOverlap(assignment.startSlot, assignment.endSlot, fromSlot, toSlot),
    );
    return Array.from({ length: dayCount }, (_, index) =>
      dominantPersonOfDayFromAssignments(inMonth, firstDay + index),
    );
  }, [assignments, year, month, firstDay, dayCount]);

  return (
    <button
      type="button"
      aria-label={formatMonthYear(year, month)}
      onClick={() => onSelect(year, month)}
      className="block text-left"
    >
      <span className="mb-1 block text-duration font-semibold text-fg-secondary">
        {MONTHS_SHORT[month - 1]}
      </span>
      <span className="grid grid-cols-7 justify-items-center gap-[1.5px]">
        {Array.from({ length: weekdayOfDay(firstDay) }, (_, index) => (
          <span key={`lead-${index}`} className="size-3" />
        ))}
        {owners.map((personId, index) => {
          const person = personId === null ? null : persons.get(personId);
          return (
            <span
              key={firstDay + index}
              style={person ? personVars(person.colorIndex) : undefined}
              className={cn(
                'size-3 rounded-[2.5px]',
                person ? 'person-bg' : 'bg-free-band',
                firstDay + index === todayDay && 'ring-today-year',
              )}
            />
          );
        })}
      </span>
    </button>
  );
}
